import { uint8ToUint32Array } from "tools/util-buffers"

export const COLOR_BINS = 32
export const RGB_BINS = COLOR_BINS ** 3
export const BYTES_PER_FRAME = RGB_BINS / 8
export const HUE_BINS = 16

export type ColorIndex = {
    bytes: Uint32Array
    length: number
}

export const getFrame = (frameIndex: number, bytes: Uint32Array) => {
    const frameBytes = bytes.subarray((frameIndex * 4096) / 4, ((frameIndex + 1) * 4096) / 4)
    return colorindexFrame(frameBytes)
}

export function colorIndexFromUint8(...bytes: Uint8Array[]): ColorIndex {
    const totalLength = bytes.reduce((acc, arr) => acc + arr.byteLength, 0)
    const result = new Uint8Array(totalLength)

    let offset = 0
    for (const arr of bytes) {
        result.set(arr, offset)
        offset += arr.byteLength
    }

    return buildColorIndex(uint8ToUint32Array(result))
}

export function buildColorIndex(bytes: Uint32Array): ColorIndex {
    return {
        bytes,
        length: bytes.length / (4096 / 4),
    }
}

export function rgbIndexToRgb(set: Set<number>) {
    return Array.from(set).map((index) => {
        const r = Math.floor(index / COLOR_BINS ** 2) * 8
        const g = Math.floor((index % COLOR_BINS ** 2) / COLOR_BINS) * 8
        const b = (index % COLOR_BINS) * 8
        return [r, g, b]
    })
}

export function rgbIndexToRgbV2(cumulative: Uint32Array) {
    const nums: number[][] = []
    for (let i = 0; i < cumulative.length; i++) {
        let word = cumulative[i]
        while (word) {
            const bit = 31 - Math.clz32(word)

            const color = (i << 5) + bit

            const red = (color >> 10) << 3
            const green = ((color >> 5) & 31) << 3
            const blue = (color & 31) << 3

            nums.push([red, green, blue])

            // This was already correct for clearing the *lowest* bit
            word ^= 1 << bit
        }
    }
    return nums
}

export interface FrameIndex {
    rgb: [number, number, number][]
    hues: number[]
}

export function colorindexFrame(bytes: Uint32Array): FrameIndex | undefined {
    const { PI, floor, clz32 } = Math
    const TAU = PI * 2

    if (bytes.length !== BYTES_PER_FRAME / 4) {
        console.error("Bad colorindex frame byte length", bytes.length)
        return
    }
    const rgb: FrameIndex["rgb"] = []
    const hues = new Array(HUE_BINS).fill(0)
    for (let i = 0; i < bytes.length; i++) {
        let word = bytes[i]
        while (word) {
            const bit = 31 - clz32(word) // count leading zeros → bit position
            const color = (i << 5) + bit
            const red = (color >> 10) << 3 // divide by 1024
            const green = ((color >> 5) & 0b11111) << 3 // divide by 32 then mod 32
            const blue = (color & 0b11111) << 3 // mod 32
            rgb.push([red, green, blue])

            const [hue, chroma] = rgb2hcb(color)

            const h = floor((hue * HUE_BINS) / TAU)
            if (chroma > hues[h]) {
                hues[h] = chroma
            }

            word ^= 1 << bit
        }
    }

    return {
        rgb,
        hues,
    }
}

export function rgbSetFromFrame(bytes: Uint32Array, start: number, rgb: Set<number>) {
    const { floor } = Math

    const uint32_start_index = floor(start)
    for (let r = 0; r < COLOR_BINS; r++) {
        for (let g = 0; g < COLOR_BINS; g++) {
            for (let b = 0; b < COLOR_BINS; b++) {
                const index = r * COLOR_BINS ** 2 + g * COLOR_BINS + b
                const uint32_index = uint32_start_index + floor(index / 32)
                const bit_position = index % 32
                const mask = 1 << bit_position
                const value_at_index = bytes[uint32_index]
                if ((value_at_index & mask) !== 0) {
                    rgb.add(index)
                }
            }
        }
    }
}

export function hcb2rgb(h: number, c: number, brightness: number) {
    const { PI, sin, cos, max, min } = Math

    const hue = -PI / 4 - 2 * PI * h
    const tilt = sin(PI / 4)
    const red = min(max(brightness + c * cos(hue + PI / 4) * tilt, 0), 1) * 255
    const green = min(max(brightness + c * cos(PI / 4 - hue) * tilt, 0), 1) * 255
    const blue = min(max(brightness - c * cos(hue) * tilt, 0), 1) * 255
    return [red, green, blue]
}

const PI = Math.PI
const TAU = PI * 2
const { sqrt, atan2 } = Math
const rgbHcbCache = new Map<number, number[]>()
export function rgb2hcb(colorKey: number) {
    const cached = rgbHcbCache.get(colorKey)

    if (cached) {
        return cached
    }

    const r = (colorKey >> 10) << 3 // divide by 1024
    const g = ((colorKey >> 5) & 0b11111) << 3 // divide by 32 then mod 32
    const b = (colorKey & 0b11111) << 3 // mod 32

    const brightness = (r + g + b) / 3
    const chroma = sqrt(
        (r - brightness) * (r - brightness) +
            (g - brightness) * (g - brightness) +
            (b - brightness) * (b - brightness)
    )
    const hue = (atan2(r - b, g - b) - PI / 4 + TAU) % TAU
    const val = [hue, chroma, brightness]
    rgbHcbCache.set(colorKey, val)
    return val
}
